import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { DateTimezoneService } from '@services/date-timezone.service';
import { cloneDeep, isEmpty } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';

export enum enumManagementSearchKey {
  ISF = 'ISF_MANAGEMENT_SEARCH',
  ES = 'ES_MANAGEMENT_SEARCH',
  ORGANIZATION = 'ORGANIZATION_MANAGEMENT_SEARCH',
  COMPANY = 'COMPANY_MANAGEMENT_SEARCH',
  BILLING = 'BILLING_MANAGEMENT_SEARCH',
  BILLING_REDESIGN = 'BILLING_REDESIGN_MANAGEMENT_SEARCH',
  USER = 'USER_MANAGEMENT_SEARCH',
  DASHBOARD_MANAGEMENT = 'DASHBOARD_MANAGEMENT_SEARCH',
  DOCUMENT_MANAGEMENT = 'DOCUMENT_MANAGEMENT_SEARCH',
  BOND = 'BOND_SEARCH',
  BOND_REQUEST = 'BOND_REQUEST_SEARCH',
  DIGITAL_POA = 'DIGITAL_POA_MANAGEMENT_SEARCH',
  LINE = 'LINE_SEARCH',
  PRICE_LIST = 'PRICE_LIST',
  SALEX_TAXES = 'SALEX_TAXES',
  SHIPMENT = 'SHIPMENT',
  QUOTE = 'QUOTE',
  VOYAGE = 'VOYAGE',
  MARINE_INSURANCE = 'MARINE_INSURANCE',
  BROKER_REVIEW = 'BROKER_REVIEW_SEARCH',
}

@Injectable({ providedIn: 'root' })
export class SearchHelper {
  constructor(private dateTimezoneService: DateTimezoneService) {}

  public static convertFiltersToParams(filters: any) {
    const params = cloneDeep(filters);
    for (const field in params) {
      if (params.hasOwnProperty(field)) {
        if ([undefined, null].includes(params[field])) {
          delete params[field];
          continue;
        }
        if (params[field] instanceof Date) {
          params[field] = params[field].toISOString();
        }
      }
    }
    return params;
  }

  public convertFiltersToParamsForQueryString(filters: any) {
    const params = cloneDeep(filters);
    for (const field in params) {
      if (params.hasOwnProperty(field)) {
        if ([undefined, null].includes(params[field])) {
          delete params[field];
          continue;
        }
        if (
          params[field] &&
          !isNaN(new Date(params[field]).getTime()) &&
          typeof params[field] === 'string' &&
          Number(params[field]).toString() !== params[field].toString() &&
          !String(params[field]).includes('+')
        ) {
          const dateLength: number = 10; // YYYY-MM-DD for day before fix
          params[field] = this.dateTimezoneService.formatInUTC(
            params[field]?.length === dateLength ? params[field] + 'T00:00:00.000000-00:00' : params[field],
            'YYYY-MM-DD',
          );
        }
      }
    }

    return params;
  }

  public static getLastSearch(key: enumManagementSearchKey): any {
    const search: any = JSON.parse(localStorage.getItem(key));
    const parsedSearch = this.parseSearch(search);
    if (!parsedSearch || Object.values(parsedSearch).every((value) => isEmpty(value))) {
      return null;
    }
    return parsedSearch;
  }

  public static setLastSearch(key: enumManagementSearchKey, search: any): void {
    localStorage.setItem(key, JSON.stringify(search));
  }

  public static parseSearch(search: any): any {
    if (search && Object.keys(search).length) {
      for (const field in search) {
        // #10018 ps: search function for admin needs a deep refactoring
        if (search.hasOwnProperty(field) && !UtilsHelper.isNumber(search[field]) && (search[field] === true || search[field] === false)) {
          search[field] = search[field] === true;
          continue;
        }

        if (!search.hasOwnProperty(field) || !search[field] || UtilsHelper.isNumber(search[field])) {
          continue;
        }
        if (!isNaN(+search[field])) {
          search[field] = +search[field];
          continue;
        }
      }
    }
    return search;
  }

  public parseSearchFromQueryString(search: any): any {
    if (search && Object.keys(search).length) {
      for (const field in search) {
        if (!search.hasOwnProperty(field) || !search[field] || UtilsHelper.isNumber(search[field])) {
          continue;
        }
        if (!isNaN(+search[field])) {
          search[field] = +search[field];
          continue;
        }
        if (this.isMatchQueryDateString(search[field])) {
          const dateLength: number = 10; // YYYY-MM-DD for day before fix
          search[field] = this.dateTimezoneService.formatInUTC(
            search[field]?.length === dateLength ? search[field] + 'T00:00:00.000000-00:00' : search[field],
          );
        }
      }
    }
    return search;
  }

  private isMatchQueryDateString(date: string): boolean {
    if (!date || (!!date && typeof date !== 'string')) {
      return false;
    }

    return !!date?.match(/^\d{4}\-(0?[1-9]|1[012])\-(0?[1-9]|[12][0-9]|3[01])$/);
  }

  private getDateParts(date: string): { year: number; month: number; day: number } {
    if (this.isMatchQueryDateString(date)) {
      const year = +date.slice(0, 4);
      const month = Number(date.slice(5, 7)) - 1;
      const day = +date.slice(8, 10);
      return { year, month, day };
    } else {
      throwError('Invalid date format');
    }
  }
}
